import { times } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { User } from 'api/models';
import {
  ApiTimesheetStatus,
  TimesheetAPI,
  TimesheetCalendarEntryRequest,
  TimesheetEntryType,
} from 'api/timesheets';
import { floatUid } from 'utils/uid';
import {
  TimeEntryState,
  TimesheetCalendarIntegrationEntry,
  TimesheetWeeklyRow,
} from '../../../types';
import { SelectedActivityRowData } from './activity-table';

const convertRowDataToTimesheetEntry = (
  rowData: SelectedActivityRowData,
  userId: number,
): TimesheetCalendarIntegrationEntry => ({
  activityName: rowData.eventName,
  billable: rowData.billable || false,
  date: moment(rowData.startTime).format('YYYY-MM-DD'),
  inState: TimeEntryState.Creating,
  minutes: rowData.duration,
  project: {
    id: rowData.projects.value as number,
    name: rowData.projects.label,
    private: false,
  },
  sourceId: rowData.eventId,
  sourceType: rowData.sourceType,
  timeEntryId: floatUid() * -1,
  type: TimesheetEntryType.Integration,
  category: rowData.category
    ? {
        categoryId: rowData.category.value as number,
        categoryName: rowData.category.label,
      }
    : undefined,
  userId,
  status: ApiTimesheetStatus.NotSubmitted,
});

export const NON_PROJECT_ACTIVITY_ID = 1;

const convertMultiDayToSeparateEntry = (
  rows: TimesheetCalendarIntegrationEntry[],
): TimesheetCalendarIntegrationEntry[] => {
  const dayInMinutes = 24 * 60;

  return rows.reduce((pre, row) => {
    const day = Math.floor(row.minutes / dayInMinutes);
    const minutes = row.minutes % dayInMinutes;

    const actualDays = day + (minutes ? 1 : 0);
    const lastDayMinutes = minutes === 0 ? dayInMinutes - 1 : minutes;

    const newRows = times(actualDays, (idx) => ({
      ...row,
      categoryId: row.category?.categoryId,
      timeEntryId: floatUid() * -1,
      minutes: actualDays === idx + 1 ? lastDayMinutes : dayInMinutes - 1,
      date: moment(row.date).add(idx, 'day').format('YYYY-MM-DD'),
    }));

    return [...pre, ...newRows];
  }, [] as TimesheetCalendarIntegrationEntry[]);
};

export const getTimesheetEntries = (
  rows: SelectedActivityRowData[],
  userId: User['userId'],
): TimesheetCalendarIntegrationEntry[] =>
  convertMultiDayToSeparateEntry(
    rows.map((row) => convertRowDataToTimesheetEntry(row, userId)),
  );

interface RejectedEntry {
  reason: any;
  task: TimesheetCalendarIntegrationEntry & { day: string };
}
export const pushToServer = async (
  tasks: TimesheetCalendarIntegrationEntry[],
  userId: number,
): Promise<{
  fulfilled: TimesheetCalendarIntegrationEntry[];
  rejected: RejectedEntry[];
}> => {
  const payload = tasks.map(
    (task) =>
      ({
        ...task,
        projectId: task?.project?.id !== 1 ? task?.project?.id : undefined,
        entryType: TimesheetEntryType.Integration,
      } as TimesheetCalendarEntryRequest),
  );

  const responses = await TimesheetAPI.bulkAddTimeEntry(userId, {
    create: payload,
  });
  const fulfilled: TimesheetCalendarIntegrationEntry[] = [];
  const rejected: RejectedEntry[] = [];

  responses.timeEntries.forEach((response, idx) => {
    if (response.status) {
      fulfilled.push({
        ...tasks[idx],
        timeEntryId: response.timeEntryResponse.timeEntryId,
      });
    } else {
      rejected.push({
        task: { ...tasks[idx], day: moment(tasks[idx].date).format('dddd') },
        reason: {
          response: {
            data: { errors: [{ reason: 'Invalid minutes specified' }] }, // this should be handled in BE. Right now handling dummy response
          },
        },
      });
    }
  });

  return { fulfilled, rejected };
};

export const getTimeFormatter = ({ startTime, endTime }) =>
  `${moment(startTime).format('hh:mm A')} - ${moment(endTime).format(
    'hh:mm A',
  )}`;

type ProjectId = number;
export const useGetLookupWeeklyRowByEntityId = (
  timesheetWeeklyRows?: TimesheetWeeklyRow[],
): { [timeEntityId: string]: ProjectId } => {
  return useMemo(() => {
    const createLookup = (rows: TimesheetWeeklyRow[]) => {
      const lookup = {};

      rows.forEach((row) => {
        const { days, task } = row;
        const weekEntries = Object.values(days);

        weekEntries.forEach((dayEntries) => {
          dayEntries.forEach((entry) => {
            lookup[entry.timeEntryId] =
              task.project?.id || NON_PROJECT_ACTIVITY_ID;
          });
        });
      });

      return lookup;
    };

    if (!timesheetWeeklyRows) {
      return {};
    }

    return createLookup(timesheetWeeklyRows);
  }, [timesheetWeeklyRows]);
};
